// --- START OF FILE pages/ProjectManagement.js ---
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/Auth'; // Adjust path
import { Plus, Search, List, Loader2, AlertTriangle } from 'lucide-react';
import ProjectCard from '../components/ProjectCard'; // Adjust path
import AddProjectModal from '../components/AddProjectModal'; // Adjust path
import SuggestionsBanner from './Programmes/SuggestionsBanner';

const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
  : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';

// Status order for sorting
const STATUS_ORDER = {
  in_progress: 1,
  proposition: 2,
  completed: 3,
};

// Status definitions pour les boutons
const statusOptions = [
  { value: 'all', label: 'Tous Statuts' },
  { value: 'proposition', label: 'Proposition' },
  { value: 'in_progress', label: 'En Cours' },
  { value: 'completed', label: 'Terminé' },
];

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base', numeric: true });

// Tri « Mon usage » : le statut d'abord (en cours, propositions, terminés), puis dans chaque statut les fiches
// que j'ouvre le plus (30 derniers jours), les projets de mon focus et l'ordre alphabétique.
// `usage` : {projet: visites} ; `focus` : projets mentionnés dans le focus de mes rôles.
export const sortByUsage = (projects, usage = {}, focus = []) => {
  const inFocus = new Set((focus || []).map(String));
  const visits = (project) => Number(usage?.[String(project.id)]) || 0;
  const statusRank = (project) => STATUS_ORDER[project.status] || 99;
  return [...projects].sort((a, b) =>
    statusRank(a) - statusRank(b)
    || visits(b) - visits(a)
    || Number(inFocus.has(String(b.id))) - Number(inFocus.has(String(a.id)))
    || byName(a, b));
};

const sortOptions = [
  { value: 'usage', label: 'Mon usage', title: 'Par statut, puis les projets que vous ouvrez le plus (30 derniers jours), ceux de votre focus, puis par ordre alphabétique' },
  { value: 'status', label: 'Statut', title: 'En cours, propositions puis terminés ; par client' },
];


const ProjectManagement = () => {
  const { getAuthHeaders, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [slots, setSlots] = useState([]);
  const [circles, setCircles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessNotice, setAccessNotice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // --- MODIFICATION : Lire le statut depuis localStorage au montage ---
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem("projectManagement_statusFilter") || 'all';
  });
  const [circleFilter, setCircleFilter] = useState(() => {
    return localStorage.getItem("projectManagement_circleFilter") || '';
  });
  const [includeChildren, setIncludeChildren] = useState(() => {
    return localStorage.getItem("projectManagement_includeChildren") === "true";
  });
  const [showAllProjects, setShowAllProjects] = useState(() => {
    return localStorage.getItem("projectManagement_showAllProjects") === "true";
  });
  const [sortMode, setSortMode] = useState(() => {
    return localStorage.getItem("projectManagement_sort") === 'status' ? 'status' : 'usage';
  });
  const [usage, setUsage] = useState({ usage: {}, focus: [] });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // téléphone : cercles repliés derrière « Filtres »

  const displayError = (message, duration = 4000) => { setError(message); setTimeout(() => setError(null), duration); };

  // --- MODIFICATION : Sauvegarder le statut dans localStorage quand il change ---
  useEffect(() => {
    localStorage.setItem("projectManagement_statusFilter", statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem("projectManagement_circleFilter", circleFilter);
  }, [circleFilter]);

  useEffect(() => {
    localStorage.setItem(
      "projectManagement_includeChildren",
      includeChildren ? "true" : "false"
    );
  }, [includeChildren]);

  useEffect(() => {
    localStorage.setItem(
      "projectManagement_showAllProjects",
      showAllProjects ? "true" : "false"
    );
  }, [showAllProjects]);

  useEffect(() => {
    localStorage.setItem("projectManagement_sort", sortMode);
  }, [sortMode]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiBaseUrl}/home/project-usage`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data && !cancelled) setUsage({ usage: data.usage || {}, focus: data.focus || [] }); })
      .catch(() => {}); // sans usage connu : focus puis ordre alphabétique
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  useEffect(() => {
    const message = location.state?.accessDeniedMessage;
    if (!message) return;
    setAccessNotice(message);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const oldestRootCircleId = useMemo(() => {
    const roots = (circles || []).filter(
      (circle) => circle.parent_id === null || circle.parent_id === undefined
    );
    if (!roots.length) return null;
    const sorted = [...roots].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) return aTime - bTime;
      return (a.id || 0) - (b.id || 0);
    });
    return sorted[0]?.id ?? null;
  }, [circles]);

  // --- Data Fetching (inchangé) ---
  const fetchAllData = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const [projRes, clientRes, empRes, slotRes, circleRes] = await Promise.all([
        fetch(`${apiBaseUrl}/projects`, { headers: getAuthHeaders() }),
        fetch(`${apiBaseUrl}/clients`, { headers: getAuthHeaders() }),
        fetch(`${apiBaseUrl}/employees`, { headers: getAuthHeaders() }),
        fetch(`${apiBaseUrl}/slots`, { headers: getAuthHeaders() }),
        fetch(`${apiBaseUrl}/circles`, { headers: getAuthHeaders() })
      ]);

      if (!projRes.ok) throw new Error('Échec du chargement des projets');
      if (!clientRes.ok) console.warn('Could not fetch clients');
      if (!empRes.ok) console.warn('Could not fetch employees');
      if (!slotRes.ok) console.warn('Could not fetch slots');
      if (!circleRes.ok) console.warn('Could not fetch circles');

      const projData = await projRes.json();
      const clientData = clientRes.ok ? await clientRes.json() : [];
      const empData = empRes.ok ? await empRes.json() : [];
      const slotData = slotRes.ok ? await slotRes.json() : [];
      const circleData = circleRes.ok ? await circleRes.json() : [];

      setProjects(Array.isArray(projData) ? projData : []);
      setClients(Array.isArray(clientData) ? clientData : []);
      setEmployees(Array.isArray(empData) ? empData : []);
      setSlots(Array.isArray(slotData) ? slotData : []);
      setCircles(Array.isArray(circleData) ? circleData : []);

    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      setProjects([]); setClients([]); setEmployees([]); setSlots([]); setCircles([]);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);


  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);


  const circleChildrenByParent = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (!circle) return;
      const parentKey = circle.parent_id === null || circle.parent_id === undefined
        ? 'root'
        : String(circle.parent_id);
      if (!map[parentKey]) map[parentKey] = [];
      map[parentKey].push(circle);
    });
    return map;
  }, [circles]);

  const userIds = useMemo(() => {
    const ids = new Set();
    if (user?.id !== undefined && user?.id !== null) {
      ids.add(String(user.id));
    }
    if (user?.employee_id !== undefined && user?.employee_id !== null) {
      ids.add(String(user.employee_id));
    }
    return ids;
  }, [user]);

  const myCircleIds = useMemo(() => {
    if (!userIds.size) return null;
    const isUserOwner = (circle) => {
      if (!circle) return false;
      if (
        circle.owner_id !== undefined &&
        circle.owner_id !== null &&
        userIds.has(String(circle.owner_id))
      ) {
        return true;
      }
      return (
        Array.isArray(circle.owner_ids) &&
        circle.owner_ids.some((id) => userIds.has(String(id)))
      );
    };

    const hasChildren = (circleId) =>
      (circleChildrenByParent[String(circleId)] || []).length > 0;
    const isRoleCircle = (circle) =>
      circle &&
      circle.parent_id !== null &&
      circle.parent_id !== undefined &&
      !hasChildren(circle.id);

    const ownerCircleIds = new Set();
    (circles || []).forEach((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return;
      if (isUserOwner(circle)) {
        ownerCircleIds.add(String(circle.id));
      }
    });

    const memberCircleIds = new Set();
    (circles || []).forEach((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return;
      const children = circleChildrenByParent[String(circle.id)] || [];
      const hasOwnedChild = children.some(
        (child) => ownerCircleIds.has(String(child.id)),
      );
      if (hasOwnedChild) {
        memberCircleIds.add(String(circle.id));
      }
    });

    const leaderCircleIds = new Set();
    (circles || []).forEach((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return;
      if (!ownerCircleIds.has(String(circle.id))) return;
      if (!isRoleCircle(circle)) {
        leaderCircleIds.add(String(circle.id));
      }
    });

    const accessible = new Set([...ownerCircleIds, ...memberCircleIds]);
    const visited = new Set();
    const stack = Array.from(leaderCircleIds);
    while (stack.length) {
      const current = stack.pop();
      if (!current || visited.has(current)) continue;
      visited.add(current);
      accessible.add(current);
      const children = circleChildrenByParent[String(current)] || [];
      children.forEach((child) => {
        if (child?.id !== undefined && child?.id !== null) {
          stack.push(String(child.id));
        }
      });
    }

    return accessible;
  }, [circles, userIds, circleChildrenByParent]);

  const selectableCircleIds = useMemo(() => {
    if (!myCircleIds) return null;
    const ids = new Set();
    (circles || []).forEach((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return;
      if (!myCircleIds.has(String(circle.id))) return;
      const hasChildren = Boolean(
        (circleChildrenByParent[String(circle.id)] || []).length
      );
      const isRole = circle.parent_id !== null && circle.parent_id !== undefined && !hasChildren;
      if (!isRole) {
        ids.add(String(circle.id));
      }
    });
    return ids;
  }, [circles, myCircleIds, circleChildrenByParent]);

  const circleFilterIds = useMemo(() => {
    if (!circleFilter || (circles || []).length === 0) return null;
    const targetId = String(circleFilter);
    const ids = new Set([targetId]);
    if (!includeChildren) return ids;
    const stack = [targetId];
    while (stack.length) {
      const current = stack.pop();
      const children = circleChildrenByParent[String(current)] || [];
      children.forEach((child) => {
        const childId = String(child.id);
        if (!ids.has(childId)) {
          ids.add(childId);
          stack.push(childId);
        }
      });
    }
    return ids;
  }, [circleFilter, includeChildren, circleChildrenByParent]);

  const circleOptions = useMemo(() => {
    let list = (circles || []).filter((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return false;
      const hasChildren = (circleChildrenByParent[String(circle.id)] || []).length > 0;
      const isRole = circle.parent_id !== null && circle.parent_id !== undefined && !hasChildren;
      return !isRole;
    });
    if (!showAllProjects) {
      if (!selectableCircleIds) return [];
      const allowedIds = new Set(Array.from(selectableCircleIds).map(String));
      list = list.filter((circle) => allowedIds.has(String(circle.id)));
    }
    if (!list.length) return [];
    const availableIds = new Set(list.map((circle) => String(circle.id)));
    const byParent = {};
    list.forEach((circle) => {
      const key = circle.parent_id === null || circle.parent_id === undefined
        ? 'root'
        : String(circle.parent_id);
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(circle);
    });
    const roots = list.filter((circle) => {
      if (circle.parent_id === null || circle.parent_id === undefined) return true;
      return !availableIds.has(String(circle.parent_id));
    }).sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) return aTime - bTime;
      return (a.id || 0) - (b.id || 0);
    });
    const output = [];
    const walk = (circle, depth) => {
      const prefix = depth > 0 ? `${'-- '.repeat(depth)}` : '';
      const label = `${prefix}${circle.name || `Cercle #${circle.id}`}`;
      output.push({ id: circle.id, label });
      const children = (byParent[String(circle.id)] || []).slice().sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
      });
      children.forEach((child) => walk(child, depth + 1));
    };
    roots.forEach((root) => walk(root, 0));
    return output;
  }, [circles, circleChildrenByParent, selectableCircleIds, showAllProjects]);

  useEffect(() => {
    if (!circles.length) return;
    if (circleOptions.length === 0) {
      if (circleFilter) setCircleFilter('');
      return;
    }
    if (!circleFilter) return;
    const hasSelected = circleOptions.some((circle) => String(circle.id) === String(circleFilter));
    if (!hasSelected) {
      setCircleFilter('');
    }
  }, [circleOptions, circleFilter, circles.length]);

  // --- Filtering (inchangé) ---
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = searchTermLower === '' ||
        project.name?.toLowerCase().includes(searchTermLower) ||
        project.project_number?.toLowerCase().includes(searchTermLower) ||
        project.client_name?.toLowerCase().includes(searchTermLower);
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const projectCircleId = project.circle_id ?? oldestRootCircleId;
      const matchesCircle = !circleFilterIds
        ? true
        : projectCircleId !== null && projectCircleId !== undefined &&
          circleFilterIds.has(String(projectCircleId));
      const matchesOwnership = showAllProjects
        ? true
        : Boolean(
          myCircleIds &&
          projectCircleId !== null &&
          projectCircleId !== undefined &&
          myCircleIds.has(String(projectCircleId))
        );
      return matchesSearch && matchesStatus && matchesCircle && matchesOwnership;
    });
  }, [projects, searchTerm, statusFilter, circleFilterIds, oldestRootCircleId, myCircleIds, showAllProjects]);

  // --- Find Current Slot (inchangé) ---
  const currentSlot = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return slots.find(slot => {
      const start = slot.start_date ? new Date(slot.start_date) : null;
      const end = slot.end_date ? new Date(slot.end_date) : null;
      return start && end && start <= today && today <= end;
    });
  }, [slots]);

  // --- Check if project is active (inchangé) ---
  const isProjectActiveInCurrentSlot = useCallback((project, slot) => {
    if (!slot || project.status !== 'in_progress' || !Array.isArray(project.sprints)) {
      return false;
    }
    const slotStart = new Date(slot.start_date);
    const slotEnd = new Date(slot.end_date);

    return project.sprints.some(sprint => {
      const sprintStart = sprint.start_date ? new Date(sprint.start_date) : null;
      const sprintEnd = sprint.end_date ? new Date(sprint.end_date) : null;
      return sprintStart && sprintEnd && sprintStart <= slotEnd && sprintEnd >= slotStart;
    });
  }, []);

  // --- Sorting (inchangé) ---
  const sortedProjects = useMemo(() => {
    if (sortMode === 'usage') return sortByUsage(filteredProjects, usage.usage, usage.focus);
    const activeProjectMap = new Map();
    if (currentSlot && projects.length > 0 && projects[0].sprints !== undefined) {
      filteredProjects.forEach(p => {
        activeProjectMap.set(p.id, isProjectActiveInCurrentSlot(p, currentSlot));
      });
    }

    return [...filteredProjects].sort((a, b) => {
      const statusOrderA = STATUS_ORDER[a.status] || 99;
      const statusOrderB = STATUS_ORDER[b.status] || 99;
      if (statusOrderA !== statusOrderB) return statusOrderA - statusOrderB;

      if (a.status === 'in_progress' && projects[0]?.sprints !== undefined) {
        const aIsActive = activeProjectMap.get(a.id) || false;
        const bIsActive = activeProjectMap.get(b.id) || false;
        if (aIsActive !== bIsActive) return aIsActive ? -1 : 1;
      }

      const clientA = a.client_name || '';
      const clientB = b.client_name || '';
      const clientCompare = clientA.localeCompare(clientB);
      if (clientCompare !== 0) return clientCompare;

      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [filteredProjects, currentSlot, isProjectActiveInCurrentSlot, projects, sortMode, usage]);


  // --- Add Project (inchangé) ---
  const handleAddProject = async (projectData) => {
    try {
      const response = await fetch(`${apiBaseUrl}/projects`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(projectData), });
      if (!response.ok) { const errData = await response.json().catch(() => ({})); throw new Error(errData.detail || `Erreur serveur (${response.status})`); }
      fetchAllData();
      return true;
    } catch (err) { console.error("API Error adding project:", err); throw err; }
  };

  // --- Update Project Status (inchangé) ---
  const handleProjectStatusUpdate = useCallback(async (projectId, newStatus) => {
    try {
      const response = await fetch(`${apiBaseUrl}/projects/${projectId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ status: newStatus }), });
      if (!response.ok) { const errData = await response.json().catch(() => ({})); throw new Error(errData.detail || `Erreur serveur (${response.status}) lors de la mise à jour du statut.`); }
      await fetchAllData();
    } catch (err) { console.error(`API Error updating status for project ${projectId}:`, err); displayError(err.message); fetchAllData(); }
  }, [getAuthHeaders, fetchAllData]);

  const handleProjectLinkUpdate = useCallback(async (projectId, field, value) => {
    try {
      const response = await fetch(`${apiBaseUrl}/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ [field]: value }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Erreur serveur (${response.status}) lors de la mise à jour du lien.`);
      }
      await fetchAllData();
    } catch (err) {
      console.error(`API Error updating link for project ${projectId}:`, err);
      displayError(err.message);
    }
  }, [getAuthHeaders, fetchAllData]);

  // --- Render ---
  return (
    <div className="px-3 py-4 sm:p-4 md:p-8 bg-gray-50 min-h-screen">
      {/* Header (inchangé dans sa structure globale) */}
      <div className="mb-4 sm:mb-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4">
        {/* Titre ; téléphone : bouton de création et filtres à côté */}
        <div className="flex w-full flex-shrink-0 items-center justify-between gap-2 lg:w-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Mes Projets</h1>
          <div className="flex items-center gap-2 sm:hidden">
            <button type="button" onClick={() => setShowFilters((value) => !value)}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium ${showFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'}`}>
              Filtres
            </button>
            <button type="button" onClick={() => setIsAddModalOpen(true)} aria-label="Nouveau projet"
              className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-white">
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Filtre Cercle (téléphone : derrière « Filtres ») */}
        <div className={`w-full lg:flex-1 lg:max-w-xl ${showFilters ? '' : 'hidden sm:block'}`}>
          <div className="flex items-center gap-2 w-full flex-wrap">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cercle</span>
            <select
              value={circleFilter}
              onChange={(e) => setCircleFilter(e.target.value)}
              className="min-w-[180px] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs sm:text-sm focus:border-blue-500 focus:outline-none"
              disabled={circleOptions.length === 0}
              aria-label="Filtrer par cercle"
            >
              {circleOptions.length === 0 ? (
                <option value="">Aucun cercle</option>
              ) : (
                <>
                  <option value="">Tous les cercles</option>
                  {circleOptions.map((circle) => (
                    <option key={circle.id} value={circle.id}>
                      {circle.label}
                    </option>
                  ))}
                </>
              )}
            </select>
            <label className="inline-flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={includeChildren}
                onChange={(e) => setIncludeChildren(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Inclure les cercles enfants
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showAllProjects}
                onChange={(e) => setShowAllProjects(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Voir tous les projets
            </label>
          </div>
        </div>
        <div className="relative w-full sm:hidden">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input type="text" placeholder="Rechercher un projet, un client…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        {/* Actions : Filtres Boutons + Nouveau Projet */}
        <div className="flex flex-col items-stretch sm:flex-row sm:items-center justify-end gap-3 w-full lg:w-auto flex-shrink-0">
          {/* Boutons de filtre (inchangés dans le rendu, utilisent l'état statusFilter lu/sauvé) */}
          <div className="flex items-center gap-2 flex-wrap justify-start max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:pb-1">
            <div className="relative w-full sm:max-w-xs max-sm:hidden">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                placeholder="Rechercher un projet (nom, client)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
              />
            </div>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
                          ${statusFilter === option.value
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
            >
              {option.label}
            </button>
          ))}
            <label className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Tri
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                title={sortOptions.find((option) => option.value === sortMode)?.title}
                aria-label="Trier les projets"
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs sm:text-sm font-medium normal-case tracking-normal text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value} title={option.title}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
          {/* Bouton Nouveau Projet */}
          <button onClick={() => setIsAddModalOpen(true)} className="max-sm:hidden px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center text-sm font-medium w-full sm:w-auto mt-2 sm:mt-0 whitespace-nowrap">
            <Plus className="h-5 w-5 md:mr-1.5" />
            <span className="hidden md:inline">Nouveau Projet</span>
            <span className="md:hidden">Nouveau</span>
          </button>
        </div>
      </div>

      <SuggestionsBanner onOpenProject={(id) => navigate(`/project/${id}?tab=programme`)} />

      {/* Loading State (inchangé) */}
      {accessNotice && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">{accessNotice}</p>
            </div>
          </div>
        </div>
      )}
      {isLoading && (<div className="flex justify-center items-center py-10"> <Loader2 className="h-8 w-8 text-blue-500 animate-spin" /> <span className="ml-3 text-gray-600">Chargement...</span> </div>)}
      {/* Error State (inchangé) */}
      {error && !isLoading && (<div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6"> <div className="flex"> <div className="flex-shrink-0"> <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" /> </div> <div className="ml-3"> <p className="text-sm text-red-700">{error}</p> </div> </div> </div>)}

      {/* Project Grid / Empty State (inchangé) */}
      {!isLoading && !error && (
        sortedProjects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-4 md:gap-6">
            {sortedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                calculatedTurnover={project.calculated_turnover || 0}
                onStatusUpdate={handleProjectStatusUpdate}
                onLinkUpdate={handleProjectLinkUpdate}
                apiBaseUrl={apiBaseUrl}
                circles={circles}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 px-4 bg-white rounded-lg shadow border border-gray-200"> <List className="mx-auto h-12 w-12 text-gray-400" /> <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun projet trouvé</h3> <p className="mt-1 text-sm text-gray-500"> {searchTerm || statusFilter !== 'all' ? "Aucun projet ne correspond à vos critères." : "Commencez par ajouter votre premier projet."} </p> {!searchTerm && statusFilter === 'all' && (<div className="mt-6"> <button type="button" onClick={() => setIsAddModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"> <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" /> Ajouter un Projet </button> </div>)} </div>
        )
      )}

      {/* Add Project Modal (inchangé) */}
      <AddProjectModal
        isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onProjectAdd={handleAddProject}
        apiBaseUrl={apiBaseUrl} getAuthHeaders={getAuthHeaders} clients={clients} employees={employees} circles={circles}
        projects={projects}
      />
    </div>
  );
};

export default ProjectManagement;
// --- END OF FILE pages/ProjectManagement.js ---
